import React, { useEffect, useState } from "react";
import '../../Pages.css';
import Billede1 from '../../../Images/Billede1.jpg';
import HeroSection from '../../../components/HeroSection';

interface TransportDto {
    transportId: number;
    country?: string;
    arrivalDate?: string;
    arrivalTime?: string;
    pickUpDate?: string;
    pickUpTime?: string;
    arrivalFlightNo?: string;
    contactperson?: string;
    contactpersonEmail?: string;
    contactpersonMobile?: string;
    numberOfPersons?: number;
    numberOfLuggage?: number;
    pickUpPlace?: string;
    dropOffPlace?: string;
    statusTransportId?: number;
    typeTransportId?: number;
    vehicleId?: number;
    driverId?: number;
    note?: string;
    afhentet?: number;
}

interface Option_TransportStatusDto {
    value: number;
    text: string;
}

interface Option_TransportTypeDto {
    value: number;
    text: string;
}

interface Option_TransportVehicleDto {
    value: number;
    text: string;
}

interface PersonWithRelationsDto {
    personId: number;
    firstName: string;
    lastName: string;
}

const FormTransportList: React.FC = () => {
    const [transports, setTransports] = useState<TransportDto[]>([]);
    const [filteredTransports, setFilteredTransports] = useState<TransportDto[]>([]);
    const [statusOptions, setStatusOptions] = useState<Option_TransportStatusDto[]>([]);
    const [typeOptions, setTypeOptions] = useState<Option_TransportTypeDto[]>([]);
    const [vehicleOptions, setVehicleOptions] = useState<Option_TransportTypeDto[]>([]);
    const [PersonsOptions, setPersonsOptions] = useState<PersonWithRelationsDto[]>([]);

    const [editingId, setEditingId] = useState<number | null>(null);
    const [editRow, setEditRow] = useState<TransportDto | null>(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // 🔍 Søgefelter
    const [searchDate, setSearchDate] = useState("");
    const [searchStatus, setSearchStatus] = useState("");
    const [searchType, setSearchType] = useState("");
    const [searchVehicle, setSearchVehicle] = useState("");

    // =======================
    // Load transports
    // =======================
    useEffect(() => {
        const fetchTransports = async () => {
            try {
                const res = await fetch("/api/form_transport/transport");
                if (!res.ok) throw new Error("Kunne ikke hente transportdata");
                const data: TransportDto[] = await res.json();
                setTransports(data);
                setFilteredTransports(data);
            } catch (e: any) {
                setError(e.message);
            } finally {
                setLoading(false);
            }
        };
        fetchTransports();
    }, []);

    // =======================
    // Load status options
    // =======================
    useEffect(() => {
        const fetchStatus = async () => {
            try {
                const res = await fetch("/api/Option_TransportStatus");
                if (!res.ok) throw new Error("Kunne ikke hente statusmuligheder");
                const data: Option_TransportStatusDto[] = await res.json();
                setStatusOptions(data);
            } catch {
                setStatusOptions([]);
            }
        };
        fetchStatus();
    }, []);

    // =======================
    // Load type options
    // =======================
    useEffect(() => {
        const fetchType = async () => {
            try {
                const res = await fetch("/api/Option_TransportType");
                if (!res.ok) throw new Error("Kunne ikke hente typemuligheder");
                const data: Option_TransportTypeDto[] = await res.json();
                setTypeOptions(data);
            } catch {
                setTypeOptions([]);
            }
        };
        fetchType();
    }, []);

    // =======================
    // Load Vehicle options
    // =======================
    useEffect(() => {
        const fetchVehicle = async () => {
            try {
                const res = await fetch("/api/Option_TransportVehicle");
                if (!res.ok) throw new Error("Kunne ikke hente Vehicleemuligheder");
                const data: Option_TransportVehicleDto[] = await res.json();
                setVehicleOptions(data);
            } catch {
                setVehicleOptions([]);
            }
        };
        fetchVehicle();
    }, []);

    // =======================
    // Load persons
    // =======================
    useEffect(() => {
        const fetchPersons = async () => {
            try {
                const res = await fetch("/api/persons");
                if (!res.ok) throw new Error("Kunne ikke hente persons");
                const data: PersonWithRelationsDto[] = await res.json();
                setPersonsOptions(data);
            } catch {
                setPersonsOptions([]);
            }
        };
        fetchPersons();
    }, []);

    // =======================
    // Filter
    // =======================
    const applyFilter = (data: TransportDto[]) => {
        const result = data.filter(t => {
            const matchDate =
                !searchDate || t.pickUpDate?.substring(0, 10) === searchDate;

            const matchStatus =
                !searchStatus || t.statusTransportId === Number(searchStatus);

            const matchType =
                !searchType || t.typeTransportId === Number(searchType);

            const matchVehicle =
                !searchVehicle || t.vehicleId === Number(searchVehicle);

            return matchDate && matchStatus && matchType && matchVehicle;
        });

        setFilteredTransports(result);
    };

    const handleSearch = () => applyFilter(transports);

    const handleReset = () => {
        setSearchDate("");
        setSearchStatus("");
        setSearchType("");
        setSearchVehicle("");
        setFilteredTransports(transports);
    };

    // =======================
    // Editing
    // =======================
    const handleEdit = (row: TransportDto) => {
        setEditingId(row.transportId);
        setEditRow({ ...row });
    };

    const handleSave = async () => {
        if (!editRow) return;

        const payload = JSON.parse(
            JSON.stringify(editRow, (_, v) => (v === undefined ? null : v))
        );

        await fetch(`/api/form_transport/transport/${editRow.transportId}`, {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(payload),
        });

        const updated = transports.map(t =>
            t.transportId === editRow.transportId ? editRow : t
        );

        setTransports(updated);
        applyFilter(updated);
        setEditingId(null);
        setEditRow(null);
    };

    const handleCancel = () => {
        setEditingId(null);
        setEditRow(null);
    };

    if (loading) return <p style={{ fontSize: "0.9rem" }}>Indlæser transportdata...</p>;
    if (error) return <p style={{ color: "red", fontSize: "0.9rem" }}>{error}</p>;

    // =======================
    // TOTALER (baseret på filtreret tabel)
    // =======================
    const totalPersons = filteredTransports.reduce(
        (sum, t) => sum + (t.numberOfPersons ?? 0),
        0
    );

    const totalLuggage = filteredTransports.reduce(
        (sum, t) => sum + (t.numberOfLuggage ?? 0),
        0
    );

    // =======================
    // Render
    // =======================
    return (
        <div className="home" style={{ fontSize: "0.9rem" }}>
            <HeroSection
                titleEn="Transport admin"
                titleDa="Transport admin"
                descriptionEn="Administration of transport requests"
                descriptionDa="Administration af transport"
                imageSrc={Billede1}
            />

            {/* 🔍 Søgesektion */}
            <div style={{
                border: "1px solid #ccc",
                borderRadius: "8px",
                padding: "16px",
                marginBottom: "20px",
                backgroundColor: "#f9f9f9",
                maxWidth: "800px",
                fontSize: "0.9rem"
            }}>
                <h3>Søg transporter</h3>

                <div style={{ display: "flex", gap: "16px", marginBottom: "12px" }}>
                    <div style={{ flex: 1 }}>
                        <label>Dato</label>
                        <input
                            type="date"
                            value={searchDate}
                            onChange={e => setSearchDate(e.target.value)}
                            style={{ width: "100%", padding: "6px" }}
                        />
                    </div>

                    <div style={{ flex: 1 }}>
                        <label>Status</label>
                        <select
                            value={searchStatus}
                            onChange={e => setSearchStatus(e.target.value)}
                            style={{ width: "100%", padding: "6px" }}
                        >
                            <option value="">Alle</option>
                            {statusOptions.map(s => (
                                <option key={s.value} value={s.value}>
                                    {s.text}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div style={{ flex: 1 }}>
                        <label>Type</label>
                        <select
                            value={searchType}
                            onChange={e => setSearchType(e.target.value)}
                            style={{ width: "100%", padding: "6px" }}
                        >
                            <option value="">Alle</option>
                            {typeOptions.map(s => (
                                <option key={s.value} value={s.value}>
                                    {s.text}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div style={{ flex: 1 }}>
                        <label>Køretøj</label>
                        <select
                            value={searchVehicle}
                            onChange={e => setSearchVehicle(e.target.value)}
                            style={{ width: "100%", padding: "6px" }}
                        >
                            <option value="">Alle</option>
                            {vehicleOptions.map(s => (
                                <option key={s.value} value={s.value}>
                                    {s.text}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                <button onClick={handleSearch}>🔍 Søg</button>
                <button onClick={handleReset} style={{ marginLeft: "8px" }}>
                    ♻️ Nulstil
                </button>
            </div>

            {/* 📋 Tabel */}
            <table border={1} cellPadding={4} cellSpacing={0} style={{ borderCollapse: "collapse", fontSize: "0.9rem", textAlign: "left"}}>
                <thead>
                    <tr>
                        <th>ID</th>
                        <th>Land</th>
                        <th>Pickup Dato</th>
                        <th>Pickup Tid</th>
                        <th>PickUp Place</th>
                        <th>Drop-off Place</th>
                        <th>Kontaktperson</th>
                        <th>Email</th>
                        <th>Mobil</th>
                        <th>Personer</th>
                        <th>Baggage</th>
                        <th>Køretøj</th>
                        <th>Chauffør</th>
                        <th>Status</th>
                        <th>Type</th>
                        <th>Note</th>
                        <th>Handling</th>
                        <th>Afhentet</th>
                    </tr>
                </thead>
                <tbody>
                    {filteredTransports.map(t => (
                        <tr key={t.transportId}>
                            <td
                                title={`Ankomst dato: ${t.arrivalDate?.substring(0, 10) ?? "-"}\nAnkomst tid: ${t.arrivalTime ?? "-"}\nFlynr: ${t.arrivalFlightNo ?? "-"}`}
                                style={{ cursor: "help" }}
                            >
                                {t.transportId}
                            </td>
                            <td>{editingId === t.transportId
                                ? <input value={editRow?.country || ""}
                                    onChange={e => setEditRow({ ...editRow!, country: e.target.value })} />
                                : t.country}</td>

                            <td>{editingId === t.transportId
                                ? <input type="date"
                                    value={editRow?.pickUpDate?.substring(0, 10) || ""}
                                    onChange={e => setEditRow({ ...editRow!, pickUpDate: e.target.value })} />
                                : t.pickUpDate && (() => {
                                    const d = new Date(t.pickUpDate);
                                    const day = String(d.getDate()).padStart(2, "0");
                                    const month = String(d.getMonth() + 1).padStart(2, "0");
                                    const year = d.getFullYear();
                                    return `${day}-${month}-${year}`;
                                })()
                            }</td>

                            <td>{editingId === t.transportId
                                ? <input type="time"
                                    value={editRow?.pickUpTime || ""}
                                    onChange={e => setEditRow({ ...editRow!, pickUpTime: e.target.value })} />
                                : t.pickUpTime}</td>

                            <td>{editingId === t.transportId
                                ? <input value={editRow?.pickUpPlace || ""}
                                    onChange={e => setEditRow({ ...editRow!, pickUpPlace: e.target.value })} />
                                : t.pickUpPlace}</td>

                            <td>{editingId === t.transportId
                                ? <input value={editRow?.dropOffPlace || ""}
                                    onChange={e => setEditRow({ ...editRow!, dropOffPlace: e.target.value })} />
                                : t.dropOffPlace}</td>

                            <td>{editingId === t.transportId
                                ? <input value={editRow?.contactperson || ""}
                                    onChange={e => setEditRow({ ...editRow!, contactperson: e.target.value })} />
                                : t.contactperson}</td>

                            <td>{editingId === t.transportId
                                ? <input value={editRow?.contactpersonEmail || ""}
                                    onChange={e => setEditRow({ ...editRow!, contactpersonEmail: e.target.value })} />
                                : t.contactpersonEmail}</td>

                            <td>{editingId === t.transportId
                                ? <input value={editRow?.contactpersonMobile || ""}
                                    onChange={e => setEditRow({ ...editRow!, contactpersonMobile: e.target.value })} />
                                : t.contactpersonMobile}</td>

                            <td>{editingId === t.transportId
                                ? <input type="number"
                                    value={editRow?.numberOfPersons ?? 0}
                                    onChange={e => setEditRow({ ...editRow!, numberOfPersons: Number(e.target.value) })} />
                                : t.numberOfPersons}</td>

                            <td>{editingId === t.transportId
                                ? <input type="number"
                                    value={editRow?.numberOfLuggage ?? 0}
                                    onChange={e => setEditRow({ ...editRow!, numberOfLuggage: Number(e.target.value) })} />
                                : t.numberOfLuggage}</td>

                            <td>
                                {editingId === t.transportId ? (
                                    <select
                                        value={editRow?.vehicleId ?? ""}
                                        onChange={e =>
                                            setEditRow({
                                                ...editRow!,
                                                vehicleId: e.target.value === "" ? undefined : Number(e.target.value)
                                            })
                                        }
                                    >
                                        <option value="">Vælg køretøj</option>
                                        {vehicleOptions.map(s => (
                                            <option key={s.value} value={s.value}>
                                                {s.text}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    vehicleOptions.find(s => s.value === t.vehicleId)?.text || ""
                                )}
                            </td>
                            <td>
                                {editingId === t.transportId ? (
                                    <select
                                        value={editRow?.driverId ?? ""}
                                        onChange={e =>
                                            setEditRow({
                                                ...editRow!,
                                                driverId: e.target.value === "" ? undefined : Number(e.target.value)
                                            })
                                        }
                                    >
                                        <option value="">Vælg chauffør</option>
                                        {PersonsOptions.map(s => (
                                            <option key={s.personId} value={s.personId}>
                                                {s.firstName}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    PersonsOptions.find(s => s.personId === t.driverId)?.firstName || ""
                                )}
                            </td>
                            <td>
                                {editingId === t.transportId ? (
                                    <select
                                        value={editRow?.statusTransportId ?? ""}
                                        onChange={e =>
                                            setEditRow({
                                                ...editRow!,
                                                statusTransportId: e.target.value === "" ? undefined : Number(e.target.value)
                                            })
                                        }
                                    >
                                        <option value="">Vælg status</option>
                                        {statusOptions.map(s => (
                                            <option key={s.value} value={s.value}>
                                                {s.text}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    statusOptions.find(s => s.value === t.statusTransportId)?.text || ""
                                )}
                            </td>

                            <td>
                                {editingId === t.transportId ? (
                                    <select
                                        value={editRow?.typeTransportId ?? ""}
                                        onChange={e =>
                                            setEditRow({
                                                ...editRow!,
                                                typeTransportId: e.target.value === "" ? undefined : Number(e.target.value)
                                            })
                                        }
                                    >
                                        <option value="">Vælg type</option>
                                        {typeOptions.map(ty => (
                                            <option key={ty.value} value={ty.value}>
                                                {ty.text}
                                            </option>
                                        ))}
                                    </select>
                                ) : (
                                    typeOptions.find(ty => ty.value === t.typeTransportId)?.text || ""
                                )}
                            </td>

                            <td>{editingId === t.transportId
                                ? <input value={editRow?.note || ""}
                                    onChange={e => setEditRow({ ...editRow!, note: e.target.value })} />
                                : t.note}
                            </td>
                            <td>
                                {editingId === t.transportId ? (
                                    <>
                                        <button
                                            onClick={handleSave}
                                            style={{ fontSize: "0.8rem", padding: "2px 4px" }}
                                        >
                                            💾
                                        </button>
                                        <button
                                            onClick={handleCancel}
                                            style={{ fontSize: "0.8rem", padding: "2px 4px" }}
                                        >
                                            ❌
                                        </button>
                                    </>
                                ) : (
                                    <button
                                        onClick={() => handleEdit(t)}
                                        style={{ fontSize: "0.8rem", padding: "2px 4px" }}
                                    >
                                        ✏️
                                    </button>
                                )}
                            </td>
                            <td>
                                {t.afhentet && (() => {
                                    const d = new Date(t.afhentet);
                                    const day = String(d.getDate()).padStart(2, "0");
                                    const month = String(d.getMonth() + 1).padStart(2, "0");
                                    const year = d.getFullYear();
                                    const hours = String(d.getHours()).padStart(2, "0");
                                    const minutes = String(d.getMinutes()).padStart(2, "0");
                                    return `${day}-${month}-${year} ${hours}:${minutes}`;
                                })()}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            {/* 🔢 Totaler */}
            <div
                style={{
                    marginTop: "12px",
                    padding: "12px",
                    borderTop: "2px solid #333",
                    display: "flex",
                    gap: "40px",
                    fontWeight: "bold",
                    fontSize: "0.9rem"
                }}
            >
                <div>👥 Total personer: {totalPersons}</div>
                <div>🧳 Total bagage: {totalLuggage}</div>
                <div>📦 Kørsler: {filteredTransports.length}</div>
            </div>
        </div>
    );
};

export default FormTransportList;
